import { Core, Decorative } from "@faire/design-tokens";
import { useNextRouterContext } from "@faire/retailer-visitor-shared/app/_lib/routing/NextRouterContext";
import {
  ISearchSuggestionResponseKeys,
  ISuggestionEntry,
  ISuggestionEntryMixin,
  ISuggestionSearchDataType,
  ITypedSuggestionEntry,
} from "@faire/retailer-visitor-shared/components/TopSearch/ResultDropdown/__internal__/Interfaces";
import {
  ResultsDropdownSectionHeaderWrapper,
  ResultsDropdownSectionsWrapper,
  ResultsDropdownView,
} from "@faire/retailer-visitor-shared/components/TopSearch/ResultDropdown/__internal__/Styled";
import {
  ARROW_DOWN,
  ARROW_UP,
  getBrandsHeader,
  getBrandsYouMightKnowHeader,
  getRecentlyViewedHeader,
  getRecentSearchesHeader,
  getSectionIdMap,
  getSuggestedTermsHeader,
  getNaturalLanguageSearchEmptyStateHeader,
  getTopResultsHeader,
  getTrendingBrandsHeader,
  getTrendingSearchesHeader,
  KEY_EVENT_VALUE,
  MAX_DESKTOP_SUGGESTION_BRANDS_OVERFLOW,
  MAX_SEARCH_RECOMMENDATION_PER_SECTION,
  MAX_SPLIT_SECTION_RESULT_SIZE,
  MAX_SUGGESTION_ITEMS_PER_SECTION,
  MAX_SUGGESTION_TOP_RESULTS,
  RESULT_DROPDOWN_SVG_ICON_PROPS,
  RETURN_KEY,
  TAB_KEY,
} from "@faire/retailer-visitor-shared/components/TopSearch/ResultDropdown/consts";
import { ImageSearchPill } from "@faire/retailer-visitor-shared/components/TopSearch/ResultDropdown/ImageSearchPill";
import { ResultDropdownImageIconItem } from "@faire/retailer-visitor-shared/components/TopSearch/ResultDropdown/ResultDropdownImageIconItem";
import { ResultDropdownImageItem } from "@faire/retailer-visitor-shared/components/TopSearch/ResultDropdown/ResultDropdownImageItem";
import { ResultDropdownImageSearchItem } from "@faire/retailer-visitor-shared/components/TopSearch/ResultDropdown/ResultDropdownImageSearchItem";
import { ResultDropdownItem } from "@faire/retailer-visitor-shared/components/TopSearch/ResultDropdown/ResultDropdownItem";
import { ResultsDropdownSectionWrapper } from "@faire/retailer-visitor-shared/components/TopSearch/ResultDropdown/ResultDropdownSectionWrapper";
import StringHighlight from "@faire/retailer-visitor-shared/components/TopSearch/ResultDropdown/StringHighlight/index";
import {
  buildNaturalLanguageSuggestions,
  buildRecentSearches,
  buildSuggestedTerms,
  buildBrandSuggestions,
  buildTrendingSearches,
  buildProductSuggestions,
} from "@faire/retailer-visitor-shared/components/TopSearch/ResultDropdown/suggestionSectionBuilders";
import { decodeMultimodalQueryText } from "@faire/retailer-visitor-shared/features/MultimodalProductSearch/multimodalQuery";
import { useSearchSuggestionsData } from "@faire/retailer-visitor-shared/features/SearchSuggestions-v2/DataFetching/useSearchSuggestionsData";
import { useRetailerStatus } from "@faire/retailer-visitor-shared/hooks/retailer/useRetailerStatus";
import { isLoggedInRetailer } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import preloadLazySearchPage from "@faire/retailer-visitor-shared/lib/lazyPreload_DO_NOT_RENAME/searchPage";
import { localizedUrlWithQueryParams } from "@faire/retailer-visitor-shared/lib/pathUtils";
import { getRetailer } from "@faire/retailer-visitor-shared/serialized-data/getRetailer";
import {
  trackBrandClick,
  trackProductClick,
} from "@faire/retailer-visitor-shared/services/Events";
import {
  useSettingLISPHoverThreshold,
  useSettingLISPShortHoverThreshold,
} from "@faire/retailer-visitor-shared/settings/getSettingLISPHoverThreshold";
import { getSettingMaxNumBrandSuggestionsConfig } from "@faire/retailer-visitor-shared/settings/getSettingMaxNumBrandSuggestionsConfig";
import { getSettingMaxNumSearchQuerySuggestionsConfig } from "@faire/retailer-visitor-shared/settings/getSettingMaxNumSearchQuerySuggestionsConfig";
import { assignSettingR2BAwardConfiguration } from "@faire/retailer-visitor-shared/settings/getSettingR2BAwardConfiguration";
import { useSettingEnableNLSSearch } from "@faire/retailer-visitor-shared/settings/useSettingEnableNLSSearch";
import {
  useAssignSettingSearchSuggestionHoverSpeculationRelaunch,
  useSettingSearchSuggestionHoverSpeculationRelaunch,
} from "@faire/retailer-visitor-shared/settings/useSettingSearchSuggestionHoverSpeculation";
import { useR2BAwardStore } from "@faire/retailer-visitor-shared/stores/domain/useR2BAwardStore";
import { useTopOfPageContent } from "@faire/retailer-visitor-shared/stores/ui/useTopOfPageContent";
import { getDocumentOrThrow } from "@faire/web/common/globals/getDocument";
import { getGlobalProperty } from "@faire/web/common/globals/getGlobalProperty";
import { getWindow } from "@faire/web/common/globals/getWindow";
import { optimizeImageUrl } from "@faire/web/common/images/optimizeImages";
import { isTouchDevice } from "@faire/web/common/isTouchDevice";
import {
  DEPRECATED_backwardsCompatibleValues,
  StrictLocalizeFunction,
  useStrictLocalization,
} from "@faire/web/common/localization/localize";
import {
  TimingMarker,
  TimingPair,
} from "@faire/web/common/timings/TimingMarker";
import { trackEvent } from "@faire/web/common/trackEvent";
import { isNotUndefined } from "@faire/web/common/typescriptUtils";
import { isRobot } from "@faire/web/common/user-agent/isRobot";
import { Opacity } from "@faire/web/slate/Color";
import { Chevron } from "@faire/web/slate/icons/foundation/Chevron";
import { Clock } from "@faire/web/slate/icons/foundation/Clock";
import { Search } from "@faire/web/slate/icons/foundation/Search";
import { HorizontalLine, Row } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import {
  getWindowHeight,
  getWindowWidth,
} from "@faire/web/ui/hooks/useObservableWindow";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { Link } from "@faire/web/ui/LinkAnchor";
import { trackR2BReferralsEntrySearchDropdown } from "@faire/web-api--source/events/r2BReferrals/actionCategoryOther/entrySearchDropdown";
import { trackSearchSuggestionClick } from "@faire/web-api--source/events/search/click/suggestion";
import { trackSearchSearchSuggestionLoadedView } from "@faire/web-api--source/events/search/view/searchSuggestionLoaded";
import { trackSearchSuggestionsView } from "@faire/web-api--source/events/search/view/suggestions";
import { ISearchRequestReferrer } from "@faire/web-api--source/indigofair/data/ISearchRequestReferrer";
import { ISearchSuggestionHoverXP } from "@faire/web-api--source/indigofair/data/ISearchSuggestionHoverXP";
import { ISearchSuggestionResponse } from "@faire/web-api--source/indigofair/data/ISearchSuggestionResponse";
import productSearch, {
  route as searchRoute,
} from "@faire/web-api--source/routes/www/search";
import userBrandReferrals from "@faire/web-api--source/routes/www/user/brand-referrals";
import { PrefetchKind } from "next/dist/client/components/router-reducer/router-reducer-types";
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  type JSX,
} from "react";
import styled from "styled-components";

export interface IResultsDropdownProps {
  query: string;
  suggestions: ISearchSuggestionResponse;
  dontShowSearch: () => void;
  startTimingMarker?: TimingMarker;
  parent: HTMLDivElement | null;
  searchElement?: HTMLElement | null;
  submit: (query: string, submittedUrl?: string, navigate?: boolean) => void;
  cancelSearch: () => void;
  deleteRecentSearchQuery: (
    deleteQuery: string,
    deleteIndex: number,
    recentSearches: string[]
  ) => void;
  deleteIdx: number;
  recentSearches?: string[];
  loadTimingRef?: React.RefCallback<HTMLElement>;
}

export const ResultsDropdown = (
  props: Omit<IResultsDropdownProps, "suggestions">
) => {
  const { searchSuggestionResponse, startTimingMarker, query } =
    useSearchSuggestionsData();
  const recentSearches = searchSuggestionResponse.recent_searches;

  return (
    <ResultsDropdownWithoutDataFetching
      {...props}
      suggestions={searchSuggestionResponse}
      startTimingMarker={startTimingMarker}
      recentSearches={recentSearches}
      query={query || props.query}
    />
  );
};

interface ISuggestionSections {
  [key: string]: ISuggestionEntryMixin<
    Partial<ISuggestionSearchDataType> | string
  >[];
}

const ResultsDropdownWithoutDataFetching: React.FunctionComponent<
  IResultsDropdownProps
> = ({
  submit,
  parent,
  dontShowSearch,
  recentSearches,
  deleteRecentSearchQuery,
  deleteIdx,
  startTimingMarker,
  searchElement,
  cancelSearch,
  query,
  suggestions,
}) => {
  const { showR2B } = useRetailerStatus();
  const retailerToken = getRetailer()?.token;
  const viewport = useViewport();
  const { strictLocalize } = useStrictLocalization();
  const { headerHeight: topOffset } = useTopOfPageContent();
  const { r2bAdvertisedRetailerAwardFormatted } = useR2BAwardStore();

  const prevQuery = useRef<string | undefined>(undefined);
  const prevSuggestions = useRef<ISearchSuggestionResponse | undefined>(
    undefined
  );
  const [displaySeeAllResults, setDisplaySeeAllResults] = useState(false);
  const [selected, setSelected] = useState(-1);
  const parentRect = useRef(parent ? parent.getBoundingClientRect() : null);
  const currentIdx = useRef(0);
  const sectionIdMap = useRef<Record<string, string>>({});
  sectionIdMap.current = getSectionIdMap(strictLocalize);
  const shouldEnableSuggestionsImpressionAllocationV2 =
    viewport.isDesktopAndAbove && isLoggedInRetailer();

  const PREFETCH_DELAY_MS = useSettingLISPHoverThreshold();
  const PREFETCH_DELAY_MS_SHORT = useSettingLISPShortHoverThreshold();
  const PREFETCH_QPS_LIMIT = 1;
  const { prefetch: nextPrefetch } = useNextRouterContext();
  const isRSCSearchPage = isLoggedInRetailer();
  const prefetchOnHoverBucket =
    useSettingSearchSuggestionHoverSpeculationRelaunch();
  const assignPrefetchBucket =
    useAssignSettingSearchSuggestionHoverSpeculationRelaunch();
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );
  const lastPrefetchTime = React.useRef<number | undefined>(undefined);

  // this is used to reset the dropdown to top
  const reference = useRef<HTMLDivElement>(null);

  const shouldShowNLSSearchSuggestions = useSettingEnableNLSSearch();

  useEffect(() => {
    if (
      prevSuggestions.current?.request_id !== suggestions.request_id &&
      startTimingMarker
    ) {
      const timingPair: TimingPair = {
        start: startTimingMarker,
      };
      trackSearchSearchSuggestionLoadedView(timingPair);
    }
  }, [startTimingMarker, suggestions.request_id]);

  useEffect(() => {
    if (viewport.isMobile) {
      if (prevQuery.current !== query) {
        reference.current?.scrollTo?.(0, 0);
      }
    }
  }, [query, viewport.isMobile]);

  useEffect(() => {
    prevQuery.current = query;
    prevSuggestions.current = suggestions;
  }, [query, suggestions]);

  const buildSuggestions = useCallback(
    (suggestionsResult: ISearchSuggestionResponse): ISuggestionSections => {
      if (!suggestionsResult) {
        return {};
      }

      const suggestionSections: ISuggestionSections = {};

      // Empty state Suggestions
      if (!query) {
        if (shouldShowNLSSearchSuggestions) {
          suggestionSections[
            getNaturalLanguageSearchEmptyStateHeader(strictLocalize)
          ] = buildNaturalLanguageSuggestions(
            suggestionsResult.suggested_natural_language_search_queries,
            getNaturalLanguageSearchEmptyStateHeader(strictLocalize),
            suggestionsResult.request_id
          );
        }

        // Recent searches
        suggestionSections[getRecentSearchesHeader(strictLocalize)] =
          buildRecentSearches(
            suggestionsResult.recent_searches,
            getRecentSearchesHeader(strictLocalize),
            suggestionsResult.request_id
          );

        // Trending searches
        // Show new trending searches section if search recommendations is returned
        if (
          shouldShowSearchRecommendations(
            suggestionsResult.search_recommendations
          )
        ) {
          suggestionSections[getTrendingSearchesHeader(strictLocalize)] =
            buildTrendingSearches(
              suggestionsResult.search_recommendations ?? [],
              getTrendingSearchesHeader(strictLocalize),
              suggestionsResult.request_id
            ).slice(0, MAX_SEARCH_RECOMMENDATION_PER_SECTION);
        } else {
          // Show the old trending searches sections if no search recommendations are returned
          suggestionSections[getTrendingSearchesHeader(strictLocalize)] =
            searchesSuggestionArray(
              suggestionsResult.trending_searches ?? [],
              getTrendingSearchesHeader(strictLocalize),
              ISearchRequestReferrer.SearchRequestReferrerType
                .SUGGESTIONS_TRENDING_SEARCHES,
              suggestionsResult.request_id
            ).slice(0, MAX_SPLIT_SECTION_RESULT_SIZE);
        }

        // Typeahead suggestions
      } else {
        const numberOfTrendingBrands = MAX_SUGGESTION_ITEMS_PER_SECTION;
        const numberOfOverflowBrandSuggestions = Math.max(
          0,
          getSettingMaxNumSearchQuerySuggestionsConfig() -
            Object.keys(suggestionsResult.suggested_search_queries).length
        );
        const increasedNumberOfBrandSuggestions = Math.min(
          getSettingMaxNumBrandSuggestionsConfig() +
            MAX_DESKTOP_SUGGESTION_BRANDS_OVERFLOW,
          getSettingMaxNumBrandSuggestionsConfig() +
            numberOfOverflowBrandSuggestions
        );
        const maxNumberOfBrandSuggestions =
          shouldEnableSuggestionsImpressionAllocationV2
            ? increasedNumberOfBrandSuggestions
            : numberOfTrendingBrands;

        // Suggestions that go both to search and to browse with filters.
        suggestionSections[getSuggestedTermsHeader(strictLocalize)] =
          buildSuggestedTerms(
            suggestionsResult,
            getSuggestedTermsHeader(strictLocalize),
            shouldEnableSuggestionsImpressionAllocationV2,
            strictLocalize
          );

        suggestionSections[getBrandsHeader(strictLocalize)] =
          buildBrandSuggestions(
            suggestionsResult.suggested_brands ?? [],
            getBrandsHeader(strictLocalize),
            suggestionsResult.request_id
          ).slice(0, maxNumberOfBrandSuggestions);

        if (viewport.isMobile && isLoggedInRetailer()) {
          // Top Results
          suggestionSections[getTopResultsHeader(strictLocalize)] =
            buildProductSuggestions(
              suggestionsResult.top_search_results ?? [],
              getTopResultsHeader(strictLocalize)
            ).slice(0, MAX_SUGGESTION_TOP_RESULTS);
        }
      }

      setDisplaySeeAllResults(
        viewport.isMobile &&
          !!query &&
          ((suggestionSections[getSuggestedTermsHeader(strictLocalize)]
            ?.length ?? 0) > 1 ||
            (suggestionSections[getBrandsHeader(strictLocalize)]?.length ?? 0) >
              1 ||
            (isLoggedInRetailer() &&
              (suggestionSections[getTopResultsHeader(strictLocalize)]
                ?.length ?? 0) > 1))
      );

      return suggestionSections;
    },
    [
      query,
      shouldEnableSuggestionsImpressionAllocationV2,
      strictLocalize,
      viewport.isMobile,
    ]
  );

  const parsedSuggestions = useMemo(
    () => buildSuggestions(suggestions),
    [buildSuggestions, suggestions]
  );

  const shouldShowR2B = useMemo(
    () => suggestions.show_r2_b_banner,
    [suggestions]
  );

  const entryCount = entryCountFromSuggestions(parsedSuggestions);

  const getSuggestionUrl = useCallback(
    (url: string) => {
      if (!url.startsWith("/")) {
        const urlPrefix = retailerToken ? `/retailer/${retailerToken}` : "/";
        url = `${urlPrefix}${url}`;
      }
      return url.replace("//", "/");
    },
    [retailerToken]
  );

  const onSuggestionClicked = useCallback(
    (
      suggestion: ISuggestionEntry,
      navigate: boolean = true // whether to explicitly navigate to the suggestion url
    ) => {
      clearTimeout(timeoutRef.current);
      // User selected an autocompleted search query (suggested terms & recent searches)
      if (suggestion.sectionName === getSuggestedTermsHeader(strictLocalize)) {
        trackEvent(`/clicked-suggestion/query/${suggestion.searchData.query}`);

        // User select Recent searches
        // have the same event track path as suggested term because old version is doing this
      } else if (
        suggestion.sectionName === getRecentSearchesHeader(strictLocalize)
      ) {
        trackEvent(`/clicked-suggestion/query/${suggestion.label}`);
        // User select trending searches
      } else if (
        suggestion.sectionName === getTrendingSearchesHeader(strictLocalize)
      ) {
        if (
          shouldShowSearchRecommendations(suggestions?.search_recommendations)
        ) {
          trackEvent(
            `/clicked-suggestion/query/v2/trending-searches/${suggestion.label}`
          );
        } else {
          trackEvent(
            `/clicked-suggestion/query/trending-searches/${suggestion.searchData}`
          );
        }
      } else if (
        suggestion.sectionName === getTrendingBrandsHeader(strictLocalize) ||
        suggestion.sectionName === getBrandsHeader(strictLocalize)
      ) {
        trackEvent(`/clicked-suggestion/brand/${suggestion.searchData.token}`);
        trackBrandClick(
          suggestion.searchData.token ?? "",
          "header_search",
          suggestions.request_id
        );
      } else if (
        suggestion.sectionName === getRecentlyViewedHeader(strictLocalize) ||
        suggestion.sectionName === getTopResultsHeader(strictLocalize)
      ) {
        trackEvent(
          suggestion.sectionName === getRecentlyViewedHeader(strictLocalize)
            ? `/clicked-suggestion/product/recently-viewed/${suggestion.searchData.token}`
            : `/clicked-suggestion/product/top-results/${suggestion.searchData.token}`
        );
        trackProductClick(
          suggestion.searchData.token ?? "",
          "header_search",
          suggestions.request_id
        );
      }

      submit(
        suggestion.searchData.query ?? "",
        getSuggestionUrl(suggestion.url),
        navigate
      );
    },
    [
      getSuggestionUrl,
      strictLocalize,
      submit,
      suggestions.request_id,
      suggestions?.search_recommendations,
    ]
  );

  const isElementInContainer = useCallback(
    (element: HTMLElement) => {
      const {
        left: elementLeft,
        right: elementRight,
        top: elementTop,
      } = element.getBoundingClientRect();
      const {
        left: containerLeft,
        right: containerRight,
        top: containerTop,
      } = parentRect.current ?? { left: 0, right: 0, top: 0 };
      return (
        elementLeft >= containerLeft &&
        elementRight <= containerRight &&
        elementTop >= containerTop
      );
    },
    [parentRect]
  );

  const handleSuggestionBlur = useCallback(() => {
    clearTimeout(timeoutRef.current);
  }, []);

  const handleSuggestionHover = useCallback(
    (selectedIdx: number) => {
      if (selectedIdx < 0) {
        setSelected(-1);
        return;
      }
      if (selectedIdx >= entryCount) {
        setSelected(entryCount - 1);
      } else {
        setSelected(selectedIdx);
      }
      const allSuggestions = Object.values(parsedSuggestions).reduce(
        (result: ISuggestionEntry[], array: ISuggestionEntry[]) => [
          ...result,
          ...array,
        ],
        []
      );
      const currentSelection = allSuggestions[selectedIdx];

      if (isRSCSearchPage) {
        clearTimeout(timeoutRef.current);
        if (lastPrefetchTime.current) {
          const timeSinceLastPrefetch = Date.now() - lastPrefetchTime.current;
          if (timeSinceLastPrefetch < 1000 / PREFETCH_QPS_LIMIT) {
            return;
          }
        }
        assignPrefetchBucket();

        const shouldPrefetch =
          prefetchOnHoverBucket !== ISearchSuggestionHoverXP.Bucket.CONTROL;
        let prefetchDelay = 0;
        if (
          prefetchOnHoverBucket === ISearchSuggestionHoverXP.Bucket.TREATMENT_A
        ) {
          prefetchDelay = PREFETCH_DELAY_MS;
        } else if (
          prefetchOnHoverBucket === ISearchSuggestionHoverXP.Bucket.TREATMENT_B
        ) {
          prefetchDelay = PREFETCH_DELAY_MS_SHORT;
        }

        timeoutRef.current = setTimeout(() => {
          if (currentSelection?.url && shouldPrefetch) {
            nextPrefetch(currentSelection.url, {
              kind: PrefetchKind.FULL,
            });
            lastPrefetchTime.current = Date.now();
          }
        }, prefetchDelay);
      }
    },
    [
      entryCount,
      prefetchOnHoverBucket,
      assignPrefetchBucket,
      parsedSuggestions,
      nextPrefetch,
      isRSCSearchPage,
      PREFETCH_DELAY_MS,
      PREFETCH_DELAY_MS_SHORT,
    ]
  );

  const onReturnPressed = useCallback(() => {
    if (selected >= 0) {
      const allSuggestions = Object.values(parsedSuggestions).reduce(
        (result: ISuggestionEntry[], array: ISuggestionEntry[]) => [
          ...result,
          ...array,
        ],
        []
      );
      const currentSelection = allSuggestions[selected];

      if (currentSelection) {
        onSuggestionClicked(currentSelection);
      }
    } else if (query) {
      submit(query);
    }

    if (searchElement) {
      searchElement.blur();
    }
  }, [
    selected,
    query,
    searchElement,
    parsedSuggestions,
    onSuggestionClicked,
    submit,
  ]);

  const keyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.type !== KEY_EVENT_VALUE) {
        return;
      }

      if (
        event.keyCode !== ARROW_UP &&
        event.keyCode !== ARROW_DOWN &&
        event.keyCode !== RETURN_KEY &&
        event.keyCode !== TAB_KEY
      ) {
        return;
      }

      // The parent search bar is also handling RETURN_KEY to execute searches.
      // So stop propagation in cases of hitting RETURN on a suggestion.
      event.stopPropagation();

      switch (event.keyCode) {
        case ARROW_UP:
          event.preventDefault();
          handleSuggestionBlur();
          handleSuggestionHover(selected - 1);
          break;

        case ARROW_DOWN:
          event.preventDefault();
          handleSuggestionBlur();
          handleSuggestionHover(selected + 1);
          break;

        case RETURN_KEY:
          onReturnPressed();
          break;

        case TAB_KEY:
          if (!isElementInContainer(event.target as HTMLElement)) {
            dontShowSearch();
          }
          break;

        default:
          break;
      }
    },
    [
      dontShowSearch,
      isElementInContainer,
      onReturnPressed,
      selected,
      handleSuggestionHover,
      handleSuggestionBlur,
    ]
  );

  // splitting up the useeffect
  useEffect(() => {
    assignSettingR2BAwardConfiguration();
    if (getGlobalProperty("isNextJS", false) && !isRobot()) {
      nextPrefetch(searchRoute);
    } else {
      preloadLazySearchPage();
    }
  }, [nextPrefetch]);

  useEffect(() => {
    getWindow()?.addEventListener(KEY_EVENT_VALUE, keyDown, {
      capture: true,
    });

    return () => {
      getWindow()?.removeEventListener(KEY_EVENT_VALUE, keyDown, {
        capture: true,
      });
      if (isTouchDevice() && viewport.isMobile) {
        getWindow()?.removeEventListener(
          "touchstart",
          removeFocusOnTopSearchWhenTouchStart,
          {
            capture: true,
          }
        );
      }
    };
  }, [keyDown, viewport.isMobile]);

  useEffect(() => {
    parentRect.current = parent ? parent.getBoundingClientRect() : null;
  }, [parent]);

  useEffect(() => {
    if (isTouchDevice() && viewport.isMobile) {
      getWindow()?.addEventListener(
        "touchstart",
        removeFocusOnTopSearchWhenTouchStart,
        {
          capture: true,
        }
      );
    }
  }, [viewport.isMobile]);

  const haveSuggestions = () => entryCount > 0;

  const buildImageItem = useCallback(
    (
      suggestion: ISuggestionEntry,
      isProduct: boolean,
      onClick: () => void,
      shouldEnableSuggestionsImpressionAllocationV2?: boolean
    ) => {
      const indx = currentIdx.current++;
      return (
        <ResultDropdownImageItem
          key={suggestion.url + indx.toString()}
          selected={indx === selected}
          index={indx}
          onHover={handleSuggestionHover}
          onMouseLeave={handleSuggestionBlur}
          onClick={onClick}
          suggestion={suggestion}
          isProduct={isProduct}
          shouldEnableSuggestionsImpressionAllocationV2={
            shouldEnableSuggestionsImpressionAllocationV2
          }
        />
      );
    },
    [selected, handleSuggestionHover, handleSuggestionBlur]
  );

  const buildImageIconItem = useCallback(
    (suggestion: ISuggestionEntry, onClick: () => void) => {
      const indx = currentIdx.current++;
      return (
        <ResultDropdownImageIconItem
          key={suggestion.url + indx.toString()}
          suggestion={suggestion}
          selected={indx === selected}
          onHover={() => handleSuggestionHover(indx)}
          onMouseLeave={handleSuggestionBlur}
          onClick={onClick}
        />
      );
    },
    [selected, handleSuggestionHover, handleSuggestionBlur]
  );

  const buildTrendingSearchImageItem = useCallback(
    (suggestion: ISuggestionEntry) => {
      const indx = currentIdx.current++;
      return (
        <ImageSearchPill
          key={suggestion.url + indx.toString()}
          selected={indx === selected}
          onHover={() => handleSuggestionHover(indx)}
          onMouseLeave={() => {
            handleSuggestionBlur();
            handleSuggestionHover(-1);
          }}
          onClick={() => onSuggestionClicked(suggestion, false)}
          suggestion={suggestion}
          url={getSuggestionUrl(suggestion.url)}
          dataTestId="search-recommendation-pill"
        />
      );
    },
    [
      selected,
      getSuggestionUrl,
      handleSuggestionHover,
      handleSuggestionBlur,
      onSuggestionClicked,
    ]
  );

  const buildSuggestedTermsItem = useCallback(
    (
      suggestion: ISuggestionEntry,
      onClick: () => void,
      shouldEnableSuggestionsImpressionAllocationV2?: boolean
    ) => {
      const indx = currentIdx.current++;

      return (
        <ResultDropdownImageSearchItem
          key={suggestion.url + indx.toString()}
          onHover={() => handleSuggestionHover(indx)}
          onMouseLeave={handleSuggestionBlur}
          onClick={onClick}
          suggestion={suggestion}
          image={<Search {...RESULT_DROPDOWN_SVG_ICON_PROPS} />}
          reverseHighlightText={query}
          selected={indx === selected}
          shouldEnableSuggestionsImpressionAllocationV2={
            shouldEnableSuggestionsImpressionAllocationV2
          }
        />
      );
    },
    [query, selected, handleSuggestionHover, handleSuggestionBlur]
  );

  const buildFullWidthTrendingSearchesItem = useCallback(
    (suggestion: ISuggestionEntry, onClick: () => void) => {
      const indx = currentIdx.current++;
      return (
        <ResultDropdownImageSearchItem
          suggestion={suggestion}
          onHover={() => handleSuggestionHover(indx)}
          onMouseLeave={handleSuggestionBlur}
          onClick={onClick}
          image={optimizeImageUrl(
            { url: suggestion.searchData?.image?.url },
            false,
            { height: 40, width: 40, resizeMethod: "fill" }
          )}
          key={suggestion.url + indx.toString()}
          selected={indx === selected}
        />
      );
    },
    [selected, handleSuggestionHover, handleSuggestionBlur]
  );

  const buildRecentSearchesImageItem = useCallback(
    (suggestion: ISuggestionEntry) => {
      const indx = currentIdx.current++;
      return (
        <ImageSearchPill
          key={suggestion.url + indx.toString()}
          onHover={() => handleSuggestionHover(indx)}
          onMouseLeave={() => {
            handleSuggestionBlur();
            handleSuggestionHover(-1);
          }}
          onClick={() => onSuggestionClicked(suggestion, false)}
          onDelete={() =>
            deleteRecentSearchQuery(
              suggestion.label,
              indx,
              recentSearches ?? []
            )
          }
          isDeleting={deleteIdx === indx}
          suggestion={suggestion}
          selected={indx === selected}
          svgIcon={<Clock {...RESULT_DROPDOWN_SVG_ICON_PROPS} />}
          url={getSuggestionUrl(suggestion.url)}
          dataTestId="recent-search-recommendation-pill"
        />
      );
    },
    [
      deleteIdx,
      selected,
      getSuggestionUrl,
      handleSuggestionHover,
      handleSuggestionBlur,
      onSuggestionClicked,
      deleteRecentSearchQuery,
      recentSearches,
    ]
  );

  const buildFullWidthRecentSearchesItem = useCallback(
    (suggestion: ISuggestionEntry, onClick: () => void) => {
      const indx = currentIdx.current++;

      return (
        <ResultDropdownImageSearchItem
          key={suggestion.url + indx.toString()}
          onHover={() => handleSuggestionHover(indx)}
          onMouseLeave={handleSuggestionBlur}
          onClick={onClick}
          onDelete={() =>
            deleteRecentSearchQuery(
              suggestion.label,
              indx,
              recentSearches ?? []
            )
          }
          isDeleting={deleteIdx === indx}
          suggestion={suggestion}
          image={<Clock {...RESULT_DROPDOWN_SVG_ICON_PROPS} />}
          selected={indx === selected}
        />
      );
    },
    [
      deleteIdx,
      deleteRecentSearchQuery,
      recentSearches,
      selected,
      handleSuggestionHover,
      handleSuggestionBlur,
    ]
  );

  const trackView = useCallback(
    (
      sectionHeader: string,
      suggestionArray:
        | ISuggestionEntry[]
        | ITypedSuggestionEntry<ISearchSuggestionResponseKeys>[],
      sectionIndex: number
    ): void => {
      const requestId = suggestionArray[0]?.requestID;

      if (shouldPerformPeteEventTracking(sectionHeader, strictLocalize)) {
        const containerName = getContainerName(sectionHeader, strictLocalize);
        const suggestionEventTokenArray = suggestionArray
          .map((suggestion) => {
            return getSuggestionEventToken(
              sectionHeader,
              suggestion,
              strictLocalize
            );
          })
          .filter(isNotUndefined);

        if (suggestionEventTokenArray.length > 0) {
          const token = `${query}|${suggestionEventTokenArray.join("|")}`;

          trackSearchSuggestionsView(
            `${containerName}|${sectionIndex}`,
            requestId ?? "",
            token
          );
        }
      }
    },
    [query, strictLocalize]
  );

  const trackClick = useCallback(
    (
      sectionHeader: string,
      sectionIndex: number,
      suggestion: ISuggestionEntry,
      suggestionIndex: number,
      navigate: boolean = true
    ): void => {
      const requestId = suggestion.requestID;

      if (shouldPerformPeteEventTracking(sectionHeader, strictLocalize)) {
        const suggestionEventToken = getSuggestionEventToken(
          sectionHeader,
          suggestion,
          strictLocalize
        );

        if (suggestionEventToken !== undefined) {
          const containerName = getContainerName(sectionHeader, strictLocalize);
          const token = `${query}|${suggestionEventToken}|${suggestionIndex}`;

          trackSearchSuggestionClick(
            `${containerName}|${sectionIndex}`,
            requestId ?? "",
            token
          );
        }
      }

      /**
       * Perform legacy event tracking.
       * We will eventually refactor all of the legacy event tracking with PETE.
       */
      onSuggestionClicked(suggestion, navigate);
    },
    [onSuggestionClicked, query, strictLocalize]
  );

  const renderDropdownSection = useCallback(
    (
      sectionHeader: string,
      suggestionArray:
        | ISuggestionEntry[]
        | ITypedSuggestionEntry<ISearchSuggestionResponseKeys>[],
      sectionIndex: number
    ) => {
      // Check to see if search recommendations is returned
      if (
        sectionHeader === getTrendingSearchesHeader(strictLocalize) &&
        shouldShowSearchRecommendations(suggestions?.search_recommendations)
      ) {
        if (viewport.isTabletAndAbove) {
          return (
            <BrandsYouMightKnowRow
              sectionHeader={sectionHeader}
              suggestionArray={suggestionArray}
              trackView={trackView}
              sectionIndex={sectionIndex}
              buildTrendingSearchImageItem={buildTrendingSearchImageItem}
            />
          );
        }
      } else if (
        viewport.isTabletAndAbove &&
        sectionHeader === getRecentSearchesHeader(strictLocalize)
      ) {
        return (
          <BrandsYouMightKnowRow
            sectionHeader={sectionHeader}
            suggestionArray={suggestionArray}
            trackView={trackView}
            sectionIndex={sectionIndex}
            buildRecentSearchesImageItem={buildRecentSearchesImageItem}
          />
        );
      }
      if (
        sectionHeader === getBrandsYouMightKnowHeader(strictLocalize) &&
        viewport.isTabletAndAbove
      ) {
        return (
          <BrandsYouMightKnowRow
            sectionHeader={sectionHeader}
            suggestionArray={suggestionArray}
            trackView={trackView}
            sectionIndex={sectionIndex}
            buildImageIconItem={buildImageIconItem}
            trackClick={trackClick}
          />
        );
      }
      return (
        <ResultsDropdownSectionWrapper
          sectionHeader={sectionHeader}
          suggestionArray={suggestionArray}
          sectionIndex={sectionIndex}
          trackView={trackView}
        >
          <ul data-test-id={sectionHeader.toLowerCase().replace(" ", "-")}>
            {suggestionArray.map(
              (suggestion: ISuggestionEntryMixin, suggestionIndex: number) => {
                const trackClickCallback = () =>
                  trackClick(
                    sectionHeader,
                    sectionIndex,
                    suggestion,
                    suggestionIndex,
                    false
                  );
                switch (sectionHeader) {
                  case getRecentSearchesHeader(strictLocalize):
                    return buildFullWidthRecentSearchesItem(
                      suggestion,
                      trackClickCallback
                    );
                  case getBrandsYouMightKnowHeader(strictLocalize):
                    return buildImageIconItem(suggestion, trackClickCallback);
                  case getTopResultsHeader(strictLocalize):
                  case getRecentlyViewedHeader(strictLocalize):
                    return buildImageItem(suggestion, true, trackClickCallback);
                  case getTrendingBrandsHeader(strictLocalize):
                  case getBrandsHeader(strictLocalize):
                    return buildImageItem(
                      suggestion,
                      false,
                      trackClickCallback,
                      shouldEnableSuggestionsImpressionAllocationV2
                    );
                  case getTrendingSearchesHeader(strictLocalize):
                    return buildFullWidthTrendingSearchesItem(
                      suggestion,
                      trackClickCallback
                    );
                  case getSuggestedTermsHeader(strictLocalize):
                    return buildSuggestedTermsItem(
                      suggestion,
                      trackClickCallback,
                      shouldEnableSuggestionsImpressionAllocationV2
                    );
                  case getNaturalLanguageSearchEmptyStateHeader(strictLocalize):
                    return buildSuggestedTermsItem(
                      suggestion,
                      trackClickCallback,
                      shouldEnableSuggestionsImpressionAllocationV2
                    );
                  default:
                    currentIdx.current++;
                    return (
                      <ResultDropdownItem
                        key={suggestion.url + currentIdx.toString()}
                        index={currentIdx.current}
                        onHover={handleSuggestionHover}
                        onMouseLeave={handleSuggestionBlur}
                        onClick={onSuggestionClicked}
                        suggestion={suggestion}
                      >
                        <StringHighlight
                          message={suggestion.label}
                          highlight={query}
                        />
                      </ResultDropdownItem>
                    );
                }
              }
            )}
          </ul>
        </ResultsDropdownSectionWrapper>
      );
    },
    [
      strictLocalize,
      suggestions?.search_recommendations,
      viewport.isTabletAndAbove,
      trackView,
      buildTrendingSearchImageItem,
      buildRecentSearchesImageItem,
      buildImageIconItem,
      trackClick,
      buildFullWidthRecentSearchesItem,
      buildImageItem,
      shouldEnableSuggestionsImpressionAllocationV2,
      buildFullWidthTrendingSearchesItem,
      buildSuggestedTermsItem,
      handleSuggestionHover,
      handleSuggestionBlur,
      onSuggestionClicked,
      query,
    ]
  );

  const getHeading = (sectionHeader: string) => (
    <ResultsDropdownSectionHeaderWrapper>
      <Spacer width="2x" />
      <Typography variant="paragraphSansMedium" role="heading">
        {sectionHeader}
      </Typography>
    </ResultsDropdownSectionHeaderWrapper>
  );

  const buildDropdownSections = useCallback(
    (
      sectionHeader: string,
      suggestionArray:
        | ISuggestionEntry[]
        | ITypedSuggestionEntry<ISearchSuggestionResponseKeys>[],
      sectionIndex: number
    ) => {
      const shouldShowSectionHeader =
        sectionHeader &&
        sectionHeader !== getSuggestedTermsHeader(strictLocalize);

      return (
        <ResultsDropdownSectionsWrapper
          key={sectionHeader + sectionIndex.toString()}
        >
          {shouldShowSectionHeader ? getHeading(sectionHeader) : null}
          <div>
            {renderDropdownSection(
              sectionHeader,
              suggestionArray,
              sectionIndex
            )}
          </div>
        </ResultsDropdownSectionsWrapper>
      );
    },
    [strictLocalize, renderDropdownSection]
  );

  const suggestionSections = parsedSuggestions;

  const contents = useMemo(() => {
    return Object.entries(suggestionSections)
      .filter(
        ([_, suggestions]: [string, ISuggestionEntryMixin[]]) =>
          suggestions.length > 0
      )
      .map(
        (
          [sectionHeader, suggestions]: [string, ISuggestionEntryMixin[]],
          sectionIndex: number
        ) => buildDropdownSections(sectionHeader, suggestions, sectionIndex)
      );
  }, [buildDropdownSections, suggestionSections]);

  currentIdx.current = 0;

  if (!haveSuggestions()) {
    return null;
  }
  currentIdx.current = 0;

  const viewProps: {
    $top: number;
    $width?: number;
  } = {
    $top: 60,
    $width: 500,
  };

  if (parentRect && parentRect.current) {
    viewProps.$top = parentRect.current.height + 22;
    viewProps.$width = parentRect.current.width;
  }

  const showBackgroundOverlay: boolean =
    viewport.isTabletAndAbove || shouldEnableSuggestionsImpressionAllocationV2;
  const pageHeight: number = getWindowHeight() - topOffset;
  // Prefer to use the getWindow()?.document.body.scrollWidth() width of the viewport (if it exists), as this doesn't include the width of the scrollbar
  const pageWidth: number =
    getWindow()?.document.body.scrollWidth ?? getWindowWidth();

  return (
    <>
      {showBackgroundOverlay ? (
        <div
          style={{
            position: "absolute",
            top: `${topOffset}px`,
            left: 0,
            width: `${pageWidth}px`,
            height: `${pageHeight}px`,
            backgroundColor: Core.surface.hover + Opacity.O_50,
            zIndex: -1,
          }}
          onClick={() => cancelSearch()}
        />
      ) : null}
      <ResultsDropdownView {...viewProps} ref={reference}>
        {contents}
        {displaySeeAllResults ? (
          <AllResultWrapper onClick={() => submit(query)}>
            <StrictLocalMsg
              defaultMessage={'See all results for "{query}"'}
              values={DEPRECATED_backwardsCompatibleValues({
                query: query,
              })}
            />
            <Chevron
              width="10px"
              strokeWidth="2.5px"
              direction="right"
              stroke={Core.icon.subdued}
            />
          </AllResultWrapper>
        ) : null}
        {!isLoggedInRetailer() ? <Spacer height={25} /> : null}
        {isLoggedInRetailer() && showR2B && shouldShowR2B ? (
          <>
            <R2MDivider>
              <HorizontalLine />
            </R2MDivider>
            <Link
              to={userBrandReferrals()}
              onClick={() => {
                cancelSearch();
                trackR2BReferralsEntrySearchDropdown();
              }}
            >
              <R2MPromoText
                onMouseOver={() => handleSuggestionHover(-1)}
                onMouseLeave={handleSuggestionBlur}
              >
                <Typography>
                  <StrictLocalMsg
                    defaultMessage="Not finding what you need?"
                    description={{
                      text: "Message at the end of a search list",
                    }}
                  />
                </Typography>
                <Spacer size="1x" />
                <Typography
                  color={Decorative.shade.orange}
                  variant="paragraphSansMedium"
                >
                  <StrictLocalMsg
                    defaultMessage="Refer brands and earn up to {amountMoney}"
                    description={{
                      text: "Call to action that links to brand referral page",
                    }}
                    values={{
                      amountMoney: r2bAdvertisedRetailerAwardFormatted,
                    }}
                  />
                </Typography>
              </R2MPromoText>
            </Link>
          </>
        ) : (
          <Spacer height="2x" />
        )}
        {viewport.isMobile ? <Spacer height={100} /> : null}
      </ResultsDropdownView>
    </>
  );
};

const shouldShowSearchRecommendations = (
  searchRecommendations:
    | ISearchSuggestionResponse.ISearchRecommendation[]
    | undefined
) => searchRecommendations && searchRecommendations?.length > 0;
const searchesSuggestionArray = (
  searchesArray: string[],
  sectionName: string,
  type: ISearchRequestReferrer.SearchRequestReferrerType,
  requestId: string | undefined
): ISuggestionEntry<string>[] =>
  searchesArray && searchesArray.length > 0
    ? searchesArray.map((searchesValue: string) => ({
        label: decodeMultimodalQueryText(searchesValue),
        url: localizedUrlWithQueryParams(productSearch({ q: searchesValue }), {
          refType: type,
          refReqId: requestId,
        }),
        sectionName,
        searchData: searchesValue,
        requestID: requestId,
      }))
    : [];

const entryCountFromSuggestions = (
  suggestions: ISuggestionSections
): number => {
  let total = 0;
  Object.values(suggestions).forEach((suggestion) => {
    total += suggestion.length;
  });
  return total;
};

const removeFocusOnTopSearchWhenTouchStart = () => {
  const topSearchElement = getDocumentOrThrow().getElementById("top-search");
  topSearchElement?.blur();
};

const shouldPerformPeteEventTracking = (
  sectionHeader: string,
  strictLocalize: StrictLocalizeFunction
): boolean => {
  return (
    sectionHeader === getTrendingSearchesHeader(strictLocalize) ||
    sectionHeader === getRecentSearchesHeader(strictLocalize) ||
    sectionHeader === getBrandsHeader(strictLocalize) ||
    sectionHeader === getTrendingBrandsHeader(strictLocalize) ||
    sectionHeader === getSuggestedTermsHeader(strictLocalize) ||
    sectionHeader === getBrandsYouMightKnowHeader(strictLocalize) ||
    sectionHeader === getNaturalLanguageSearchEmptyStateHeader(strictLocalize)
  );
};

const getContainerName = (
  sectionHeader: string,
  strictLocalize: StrictLocalizeFunction
): string => {
  // TODO:: (kiminno) Refactor the suggestions to return the container names from BE instead.
  const DEFAULT_CONTAINER_NAME = "suggestions_default_container_name";
  const SEARCH_SUGGESTION_TRENDING_SEARCHES_CONTAINER_NAME =
    "SUGGESTIONS_TRENDING_SEARCHES";
  const RECENT_SEARCHES_CONTAINER_NAME = "SUGGESTIONS_RECENT_SEARCHES";
  const SEARCH_SUGGESTION_BRAND_SUGGESTIONS = "SUGGESTIONS_BRANDS";
  const SEARCH_SUGGESTION_QUERY_SUGGESTIONS = "SUGGESTIONS_SEARCH_QUERIES";
  const SEARCH_SUGGESTION_BRANDS_YOU_MAY_KNOW =
    "SUGGESTIONS_BRANDS_YOU_MAY_KNOW";

  switch (sectionHeader) {
    case getRecentSearchesHeader(strictLocalize):
      return RECENT_SEARCHES_CONTAINER_NAME;
    case getSuggestedTermsHeader(strictLocalize):
      return SEARCH_SUGGESTION_QUERY_SUGGESTIONS;
    case getTrendingSearchesHeader(strictLocalize):
      return SEARCH_SUGGESTION_TRENDING_SEARCHES_CONTAINER_NAME;
    case getBrandsHeader(strictLocalize):
      return SEARCH_SUGGESTION_BRAND_SUGGESTIONS;
    case getTrendingBrandsHeader(strictLocalize):
      return SEARCH_SUGGESTION_BRAND_SUGGESTIONS;
    case getBrandsYouMightKnowHeader(strictLocalize):
      return SEARCH_SUGGESTION_BRANDS_YOU_MAY_KNOW;
    case getNaturalLanguageSearchEmptyStateHeader(strictLocalize):
      return ISearchRequestReferrer.SearchRequestReferrerType
        .SUGGESTIONS_NATURAL_LANGUAGE_SEARCH_EMPTY_STATE;
    default:
      return DEFAULT_CONTAINER_NAME;
  }
};

const getSuggestionEventToken = (
  sectionHeader: string,
  suggestion: ISuggestionEntry,
  strictLocalize: StrictLocalizeFunction
): string | undefined => {
  switch (sectionHeader) {
    case getRecentSearchesHeader(strictLocalize): {
      return `${suggestion.label}`;
    }
    case getSuggestedTermsHeader(strictLocalize): {
      return `${suggestion.searchData.query}`;
    }
    case getTrendingSearchesHeader(strictLocalize): {
      return `${suggestion.label}`;
    }
    case getBrandsYouMightKnowHeader(strictLocalize):
    case getBrandsHeader(strictLocalize): {
      return `${suggestion.searchData.token}`;
    }
    case getTrendingBrandsHeader(strictLocalize): {
      return `${suggestion.label}`;
    }
    case getNaturalLanguageSearchEmptyStateHeader(strictLocalize): {
      return `${suggestion.label}`;
    }
    default:
      return "";
  }
};

const BrandsYouMightKnowRow: React.FC<{
  sectionHeader: string;
  suggestionArray: ISuggestionEntry[];
  sectionIndex: number;
  trackView: (
    sectionHeader: string,
    suggestionArray: ISuggestionEntry[],
    sectionIndex: number
  ) => void;
  buildRecentSearchesImageItem?: (suggestion: ISuggestionEntry) => JSX.Element;
  buildTrendingSearchImageItem?: (suggestion: ISuggestionEntry) => JSX.Element;
  buildImageIconItem?: (
    suggestion: ISuggestionEntry,
    onClick: () => void
  ) => JSX.Element;
  trackClick?: (
    sectionHeader: string,
    sectionIndex: number,
    suggestion: ISuggestionEntry,
    suggestionIndex: number,
    navigate?: boolean
  ) => void;
}> = ({
  sectionHeader,
  suggestionArray,
  sectionIndex,
  trackView,
  buildRecentSearchesImageItem,
  buildTrendingSearchImageItem,
  buildImageIconItem,
  trackClick,
}) => {
  const ref = useDoActionWhenVisible(() => {
    trackView(sectionHeader, suggestionArray, sectionIndex);
  });

  return trackClick && buildImageIconItem ? (
    <StyledRow align="center" tabIndex={-1} ref={ref}>
      {suggestionArray.map(
        (suggestion: ISuggestionEntry, suggestionIndex: number) => {
          return buildImageIconItem(suggestion, () =>
            trackClick(
              sectionHeader,
              sectionIndex,
              suggestion,
              suggestionIndex,
              false
            )
          );
        }
      )}
    </StyledRow>
  ) : (
    <StyledRow align="center" tabIndex={-1} ref={ref}>
      {suggestionArray.map((suggestion: ISuggestionEntry) => {
        if (buildRecentSearchesImageItem) {
          return buildRecentSearchesImageItem(suggestion);
        } else if (buildTrendingSearchImageItem) {
          return buildTrendingSearchImageItem(suggestion);
        }
        return null;
      })}
    </StyledRow>
  );
};

const AllResultWrapper = styled(Row).attrs({
  justify: "space-between",
  align: "center",
})`
  position: relative;
  height: 64px;
  padding: 22px 16px 23px 19px;
`;

const R2MPromoText = styled.div`
  text-align: center;
  padding: ${getSpacing("2x")} !important;

  &:hover {
    background-color: rgba(234, 234, 234, 0.5);
  }
`;

const R2MDivider = styled.div`
  padding: 0 8px;
`;

const StyledRow = styled(Row)`
  flex-wrap: wrap;
  padding: 2px 10px 2px 10px;
`;
